if (!customElements.get('review-slider')) {
  class ReviewSlider extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-review-slider-rail]');
      this.controls = this.querySelector('[data-review-slider-controls]');
      this.previousButton = this.querySelector('[data-review-slider-previous]');
      this.nextButton = this.querySelector('[data-review-slider-next]');
      if (!this.rail || !this.controls || !this.previousButton || !this.nextButton) return;

      this.items = [...this.rail.querySelectorAll('[data-review-slider-item]')];
      this.reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.updateFrame = null;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.previousButton.addEventListener('click', () => this.scrollByItem(-1), { signal });
      this.nextButton.addEventListener('click', () => this.scrollByItem(1), { signal });
      this.rail.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true, signal });
      this.addEventListener('shopify:block:select', (event) => this.scrollToItem(event.target), { signal });

      this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
      this.resizeObserver.observe(this.rail);

      this.viewObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => entry.target.toggleAttribute('data-in-view', entry.isIntersecting));
      }, { root: this.rail, threshold: 0.6 });
      this.items.forEach((item) => this.viewObserver.observe(item));

      this.setAttribute('data-ready', '');
      this.scheduleUpdate();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.viewObserver?.disconnect();
      if (this.updateFrame !== null) cancelAnimationFrame(this.updateFrame);
    }

    scheduleUpdate() {
      if (this.updateFrame !== null) return;
      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.updateControls();
      });
    }

    updateControls() {
      const distance = this.rail.scrollWidth - this.rail.clientWidth;
      const offset = Math.abs(this.rail.scrollLeft);
      const tolerance = 2;

      this.controls.hidden = distance <= 1;
      this.previousButton.disabled = offset <= tolerance;
      this.nextButton.disabled = offset >= distance - tolerance;
    }

    // The distance from the rail's start edge to an item's start edge, in the rail's scroll direction.
    offsetOf(item) {
      const isRtl = getComputedStyle(this.rail).direction === 'rtl';
      const railRect = this.rail.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      return isRtl ? itemRect.right - railRect.right : itemRect.left - railRect.left;
    }

    scrollToItem(item) {
      this.rail.scrollBy({
        left: this.offsetOf(item),
        behavior: this.reducedMotionMedia.matches ? 'auto' : 'smooth',
      });
    }

    scrollByItem(direction) {
      const offsets = this.items.map((item) => Math.abs(this.offsetOf(item)));
      const current = offsets.indexOf(Math.min(...offsets));
      const target = this.items[Math.max(0, Math.min(this.items.length - 1, current + direction))];
      this.scrollToItem(target);
    }
  }

  customElements.define('review-slider', ReviewSlider);
}
